import {
  TSP,
  useAddTSPToRegionMutation,
  useAddZoneToRegionMutation,
  useRemoveTSPFromRegionMutation,
  useRemoveZoneFromRegionMutation,
  useSplitTSPInRegionMutation,
} from '../services/edgeApi';
import { useCallback } from 'react';
import { Region, RegionTSP, RegionZone } from '@/types/api/Region';
import toast from 'react-hot-toast';
import { Zone } from '@/types';
import { ZoneRegion } from '@/types/api/Zone';
import { TSPRegion } from '@/types/api/TSP';

export const useRegionManagement = () => {
  const [addPipeTrigger, { isLoading: isAddingPipe }] =
    useAddTSPToRegionMutation();
  const [removePipeTrigger, { isLoading: isRemovingPipe }] =
    useRemoveTSPFromRegionMutation();
  const [addZoneTrigger, { isLoading: isAddingZone }] =
    useAddZoneToRegionMutation();
  const [removeZoneTrigger, { isLoading: isRemovingZone }] =
    useRemoveZoneFromRegionMutation();
  const [splitTSPTrigger, { isLoading: isSplittingTSP }] =
    useSplitTSPInRegionMutation();

  const addPipeToRegion = useCallback(
    async (region: Region | ZoneRegion | TSPRegion, tsp: TSP | RegionTSP) => {
      const toastId = toast.loading(`Adding ${tsp.name} to ${region.name}...`);
      try {
        await addPipeTrigger({ regionId: region.id, tsp: tsp.id });
        toast.success(`Successfully added ${tsp.name} to ${region.name}`, {
          id: toastId,
        });
      } catch (error) {
        toast.error(`Failed to add ${tsp.name} to ${region.name}`, {
          id: toastId,
        });
        console.error('Failed to add TSP to region:', error);
      }
    },
    [addPipeTrigger],
  );

  const removePipeFromRegion = useCallback(
    async (region: Region | ZoneRegion | TSPRegion, tsp: TSP | RegionTSP) => {
      const toastId = toast.loading(
        `Removing ${tsp.name} from ${region.name}...`,
      );
      try {
        await removePipeTrigger({
          regionId: region.id,
          tsp: tsp.id,
        });
        toast.success(`Successfully removed ${tsp.name} from ${region.name}`, {
          id: toastId,
        });
      } catch (error) {
        toast.error(`Failed to remove ${tsp.name} from ${region.name}`, {
          id: toastId,
        });
        console.error('Failed to remove TSP from region:', error);
      }
    },
    [removePipeTrigger],
  );

  const addZoneToRegion = useCallback(
    async (
      region: Region | ZoneRegion | TSPRegion,
      zone: Zone | RegionZone,
    ) => {
      let toastId = toast.loading(
        `Adding ${zone.name} ${zone.id} to ${region.name}...`,
      );
      try {
        await addZoneTrigger({
          regionId: region.id,
          zone: zone.id,
        });
        toast.success(
          `Successfully added ${zone.name} ${zone.id} to ${region.name}`,
          { id: toastId },
        );
      } catch (error) {
        toast.error(`Failed to add ${zone.name} ${zone.id} to ${region.name}`, {
          id: toastId,
        });
        console.error('Failed to add Zone to region:', error);
      }
    },
    [addZoneTrigger],
  );

  const removeZoneFromRegion = useCallback(
    async (
      region: Region | ZoneRegion | TSPRegion,
      zone: Zone | RegionZone,
    ) => {
      const toastId = toast.loading(
        `Removing ${zone.name} ${zone.id} from ${region.name}...`,
      );
      try {
        await removeZoneTrigger({
          regionId: region.id,
          zone: zone.id,
        });
        toast.success(
          `Successfully removed ${zone.name} ${zone.id} from ${region.name}`,
          { id: toastId },
        );
      } catch (error) {
        toast.error(
          `Failed to remove ${zone.name} ${zone.id} from ${region.name}`,
          { id: toastId },
        );
        console.error('Failed to remove Zone from region:', error);
      }
    },
    [removeZoneTrigger],
  );

  const splitTSPInRegion = useCallback(
    async (region: Region | ZoneRegion | TSPRegion, tsp: TSP | RegionTSP) => {
      const toastId = toast.loading(
        `Splitting ${tsp.name} in ${region.name}...`,
      );
      try {
        await splitTSPTrigger({
          regionId: region.id,
          tsp: tsp.id,
        });
        toast.success(`Successfully split ${tsp.name} in ${region.name}`, {
          id: toastId,
        });
      } catch (error) {
        toast.error(`Failed to split ${tsp.name} in ${region.name}`, {
          id: toastId,
        });
        console.error('Failed to split TSP in region:', error);
      }
    },
    [splitTSPTrigger],
  );

  return {
    addPipeToRegion,
    removePipeFromRegion,
    addZoneToRegion,
    removeZoneFromRegion,
    splitTSPInRegion,
    isAddingPipe,
    isRemovingPipe,
    isAddingZone,
    isRemovingZone,
    isSplittingTSP,
    isUpdating: false,
    // isAddingPipe || isRemovingPipe || isAddingZone || isRemovingZone || isSplittingTSP,
  };
};
